import * as yup from 'yup';

import { ABOUT_MAX_LENGTH, isAboutWithinLimit } from 'utils/aboutHtml';

const baseSchema = yup.object({
  name: yup.string().required('Имя обязательно'),
  // Фамилию и дату рождения репетитор указывает в анкете после регистрации (Figma 21:9440).
  // Сохранить анкету можно и без них, но на проверку она не уйдет: шаг «Общие сведения»
  // приходит с сервера неготовым.
  surname: yup.string(),
  patronymic: yup.string(),
  birthDate: yup.string(),
  formatOnline: yup.boolean(),
  formatOffline: yup.boolean(),
  about: yup
    .string()
    .test(
      'about-length',
      `Текст "О себе" не должен превышать ${ABOUT_MAX_LENGTH} символов`,
      (value) => isAboutWithinLimit(value ?? ''),
    ),
  trialLesson: yup.string().required('Выберите вариант пробного занятия'),
});

const educationFormSchema = yup.object({
  university: yup.string().trim().required('Укажите учебное заведение'),
  faculty: yup.string().trim().required('Укажите факультет'),
  specialization: yup.string().trim().required('Укажите специализацию'),
  startYear: yup
    .string()
    .required('Укажите год поступления')
    .test('valid-start-year', 'Некорректный год поступления', (value) => {
      const year = parseInt(value || '', 10);
      const currentYear = new Date().getFullYear();
      return year >= 1970 && year <= currentYear;
    }),
  endYear: yup
    .string()
    .nullable()
    .test(
      'end-year-required',
      'Год окончания обязателен если не учусь по настоящее время',
      function (value) {
        if (this.parent.testLesson) {
          return true;
        }
        if (!value || !String(value).trim() || value === 'present') {
          return this.createError({ message: 'Выберите год окончания' });
        }
        return true;
      },
    )
    .test(
      'end-year-after-start',
      'Год окончания должен быть после года поступления',
      function (value) {
        const { startYear, testLesson } = this.parent;

        if (testLesson || value === 'present') {
          return true;
        }

        if (!startYear || !value) {
          return true;
        }

        const start = parseInt(startYear, 10);
        const end = parseInt(value, 10);

        return end >= start;
      },
    ),
  testLesson: yup.boolean(),
  token: yup.string().optional(),
  eduPhoto: yup
    .mixed()
    .nullable()
    .test(
      'diploma-required-unless-studying',
      'Загрузите документ об образовании',
      function (value) {
        // "Учусь по настоящее время" - фото диплома не обязательно
        if (this.parent.testLesson) {
          return true;
        }
        const hasFile = value instanceof File;
        const hasExisting =
          Boolean(this.parent.token) ||
          (typeof value === 'string' && value.length > 0);
        return hasFile || hasExisting;
      },
    ),
});

const educationIsBlank = (value: unknown) => {
  if (!value || typeof value !== 'object') return true;
  const row = value as Record<string, unknown>;
  const hasText = ['university', 'faculty', 'specialization', 'startYear', 'endYear'].some((key) =>
    String(row[key] ?? '').trim(),
  );
  const photo = row.eduPhoto;
  const hasPhoto =
    (typeof File !== 'undefined' && photo instanceof File) ||
    Boolean(String(row.token ?? '').trim()) ||
    (typeof photo === 'string' && photo.trim().length > 0);
  return !hasText && !hasPhoto;
};

const priceFormSchema = yup.object({
  service: yup.string().required('Услуга обязательна'),
  price: yup
    .number()
    .transform((value, original) => {
      if (typeof original !== 'string') return value;
      const cleaned = original.replace(/\s/g, '').replace(',', '.');
      if (!cleaned) return NaN;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : NaN;
    })
    .typeError('Цена должна быть числом')
    .integer('Цена должна быть целым числом')
    .min(500, 'Цена не меньше 500 рублей в час')
    .required('Цена обязательна'),
});

export const teacherEditSchema = baseSchema.concat(
  yup.object({
    educations: yup
      .array()
      .of(yup.lazy((value) => (educationIsBlank(value) ? yup.object() : educationFormSchema)))
      .max(5),
    teacher_prices: yup
      .array()
      .of(priceFormSchema)
      .min(1, 'Добавьте хотя бы одну услугу')
      .max(5)
      .test(
        'unique-services',
        'Каждую услугу можно добавить только один раз',
        (prices) => {
          if (!prices?.length) return true;
          const keys = prices
            .map((row) => String(row?.service || '').trim().toLowerCase())
            .filter(Boolean);
          return keys.length === new Set(keys).size;
        },
      ),
  }),
);
